import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CALL_STATUSES, type CallStatus } from '@/types/api';

const ORDERS = ['started_desc', 'started_asc', 'id_desc', 'id_asc'] as const;
const LIMITS = [25, 50, 100];

export default function TaskFilterStrip() {
  const { t } = useTranslation('tasks');
  const [params, setParams] = useSearchParams();

  function update(patch: Record<string, string | string[] | null>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      next.delete(k);
      if (v === null || v === undefined || v === '') continue;
      if (Array.isArray(v)) for (const x of v) next.append(k, x);
      else next.set(k, v);
    }
    next.delete('cursor'); // any filter change resets pagination
    setParams(next, { replace: true });
  }

  const phone = params.get('phone') ?? '';
  const direction = params.get('direction') ?? '';
  const order = (params.get('order') ?? 'started_desc') as (typeof ORDERS)[number];
  const limit = Number(params.get('limit') ?? '50');
  const selectedStatuses = params.getAll('status') as CallStatus[];

  function toggleStatus(s: CallStatus) {
    const next = selectedStatuses.includes(s)
      ? selectedStatuses.filter((x) => x !== s)
      : [...selectedStatuses, s];
    update({ status: next });
  }

  function orderLabelKey(o: string): string {
    // started_desc → orderStartedDesc, id_asc → orderIdAsc
    const camel = o.replace(/_(.)/g, (_, c: string) => c.toUpperCase());
    return `filters.order${camel.charAt(0).toUpperCase()}${camel.slice(1)}`;
  }

  return (
    <div className="flex flex-wrap items-end gap-2 text-xs">
      <label className="flex flex-col">
        <span className="text-muted-foreground">{t('filters.phone')}</span>
        <Input
          className="h-8 w-40"
          placeholder={t('filters.phonePlaceholder')}
          defaultValue={phone}
          onBlur={(e) => update({ phone: e.target.value || null })}
        />
      </label>
      <label className="flex flex-col">
        <span className="text-muted-foreground">{t('filters.direction')}</span>
        <select
          className="h-8 rounded-md border bg-background px-2"
          value={direction}
          onChange={(e) => update({ direction: e.target.value || null })}
        >
          <option value="">{t('filters.directionAny')}</option>
          <option value="1">{t('filters.directionIn')}</option>
          <option value="2">{t('filters.directionOut')}</option>
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-muted-foreground">{t('filters.from')}</span>
        <Input
          className="h-8 w-44"
          type="datetime-local"
          defaultValue={params.get('started_from') ?? ''}
          onBlur={(e) => update({ started_from: e.target.value || null })}
        />
      </label>
      <label className="flex flex-col">
        <span className="text-muted-foreground">{t('filters.to')}</span>
        <Input
          className="h-8 w-44"
          type="datetime-local"
          defaultValue={params.get('started_to') ?? ''}
          onBlur={(e) => update({ started_to: e.target.value || null })}
        />
      </label>
      <label className="flex flex-col">
        <span className="text-muted-foreground">{t('filters.order')}</span>
        <select
          className="h-8 rounded-md border bg-background px-2"
          value={order}
          onChange={(e) => update({ order: e.target.value })}
        >
          {ORDERS.map((o) => (
            <option key={o} value={o}>
              {t(orderLabelKey(o), { defaultValue: o })}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col">
        <span className="text-muted-foreground">{t('filters.limit')}</span>
        <select
          className="h-8 rounded-md border bg-background px-2"
          value={String(limit)}
          onChange={(e) => update({ limit: e.target.value })}
        >
          {LIMITS.map((n) => (
            <option key={n} value={String(n)}>
              {n}
            </option>
          ))}
        </select>
      </label>
      <details className="ml-2">
        <summary className="cursor-pointer text-muted-foreground">
          {t('filters.status')}: {selectedStatuses.length || t('filters.statusAny')}
        </summary>
        <div className="mt-1 grid max-w-md grid-cols-2 gap-1 rounded-md border bg-popover p-2">
          {CALL_STATUSES.map((s) => (
            <label key={s} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={selectedStatuses.includes(s)}
                onChange={() => toggleStatus(s)}
              />
              <span>{s}</span>
            </label>
          ))}
        </div>
      </details>
      {(selectedStatuses.length > 0 || phone || direction) && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setParams(new URLSearchParams(), { replace: true })}
        >
          {t('filters.reset')}
        </Button>
      )}
    </div>
  );
}
